/**
 * Maps viem / wagmi / wallet-provider errors thrown during gas estimation
 * or transaction submission into short, user-friendly sentences shown in
 * the Send screen.
 *
 * We deliberately do NOT consume `viem`'s typed error classes
 * (`InsufficientFundsError`, `UserRejectedRequestError`, …) directly:
 *
 *   - Errors that surface here may have travelled through wagmi /
 *     embedded-wallet RPCs / `JSON.stringify` round-trips, which can
 *     break `instanceof` checks.
 *   - Some wallet providers wrap or re-throw their own error subclasses.
 *
 * String-pattern matching on `name` / `shortMessage` / `message` covers
 * all of those paths. Unknown errors fall back to the raw message so we
 * never hide information the user might need to report.
 */

export interface SendErrorContext {
  /** Gas-currency symbol (e.g. "BNB", "ETH"). Used to clarify which asset is insufficient. */
  nativeSymbol?: string;
  /** Human-readable network name (e.g. "BSC Testnet"). */
  networkName?: string;
  /** The token symbol being sent (e.g. "CROSS"). */
  tokenSymbol?: string;
}

type LooseError = {
  name?: unknown;
  message?: unknown;
  shortMessage?: unknown;
  details?: unknown;
  cause?: unknown;
};

/**
 * Pulls every string field viem-style errors might carry into one
 * lowercased haystack. Walks `cause` chains because wagmi often wraps
 * the original viem error one or two layers deep.
 */
function collectErrorText(err: unknown, depth = 0): string {
  if (depth > 4 || err === null || err === undefined) return '';
  if (typeof err === 'string') return err.toLowerCase();
  if (typeof err !== 'object') return '';

  const e = err as LooseError;
  const parts: string[] = [];
  if (typeof e.name === 'string') parts.push(e.name);
  if (typeof e.shortMessage === 'string') parts.push(e.shortMessage);
  if (typeof e.message === 'string') parts.push(e.message);
  if (typeof e.details === 'string') parts.push(e.details);
  if (e.cause !== undefined) parts.push(collectErrorText(e.cause, depth + 1));

  return parts.join(' \n ').toLowerCase();
}

function rawMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const e = err as LooseError;
    if (typeof e.shortMessage === 'string') return e.shortMessage;
    if (typeof e.message === 'string') return e.message;
  }
  return '';
}

/**
 * Translates a Send-flow error into a one-line message safe to render
 * inline. Falls back to the original error message when no pattern
 * matches so the user can still file a bug report with full context.
 */
export function formatSendError(
  err: unknown,
  context: SendErrorContext = {},
  fallback = 'Failed to send transaction.',
): string {
  const haystack = collectErrorText(err);
  if (!haystack) return fallback;

  const nativeSymbol = context.nativeSymbol?.trim() || 'gas token';
  const networkName = context.networkName?.trim();

  // ── User-cancelled ────────────────────────────────────────────────────
  // Cover viem (`UserRejectedRequestError`), MetaMask 4001, embedded
  // wallet "user rejected" wording, and assorted custom shapes.
  if (
    haystack.includes('user rejected') ||
    haystack.includes('userrejected') ||
    haystack.includes('user denied') ||
    haystack.includes('user cancelled') ||
    haystack.includes('user canceled') ||
    haystack.includes('action_rejected') ||
    haystack.includes('rejected by user')
  ) {
    return 'Transaction was cancelled.';
  }

  // ── Insufficient gas-currency ────────────────────────────────────────
  // viem `InsufficientFundsError`:
  //   "The total cost (gas * gas fee + value) of executing this
  //    transaction exceeds the balance of the account."
  // Provider variants: "insufficient funds for gas", "intrinsic gas too
  // low" (different cause but same user fix on testnets).
  if (
    haystack.includes('insufficientfunds') ||
    haystack.includes('insufficient funds') ||
    haystack.includes('exceeds the balance of the account') ||
    haystack.includes('insufficient balance for transfer')
  ) {
    const where = networkName ? ` on ${networkName}` : '';
    return (
      `Not enough ${nativeSymbol} to cover the gas fee.` + 
      `Please top up your ${nativeSymbol} balance and try again.`
    );
  }

  // ── Underpriced / replacement-too-low ────────────────────────────────
  if (
    haystack.includes('transaction underpriced') ||
    haystack.includes('replacement transaction underpriced') ||
    haystack.includes('fee too low')
  ) {
    return 'Gas fee was too low for the network. Please try again.';
  }

  // ── Nonce drift ──────────────────────────────────────────────────────
  if (
    haystack.includes('nonce too low') ||
    haystack.includes('nonce has already been used') ||
    haystack.includes('nonce too high')
  ) {
    return 'Pending transactions are out of order. Refresh the page and try again.';
  }

  // ── Reverted on-chain (or pre-flight simulation revert) ──────────────
  if (
    haystack.includes('execution reverted') ||
    haystack.includes('contractfunctionrevertederror') ||
    haystack.includes('contractfunctionexecutionerror')
  ) {
    return 'The transaction was rejected by the contract. The recipient or token may be invalid.';
  }

  // ── Chain not registered in wagmi ────────────────────────────────────
  // ("Chain X is not configured" — usually a misconfigured DApp.)
  if (haystack.match(/chain \d+ is not configured/)) {
    return 'This network is not registered in the app. Contact support.';
  }

  // ── RPC / network connectivity ───────────────────────────────────────
  if (
    haystack.includes('http request failed') ||
    haystack.includes('failed to fetch') ||
    haystack.includes('network request failed') ||
    haystack.includes('timeout') ||
    haystack.includes('econnreset') ||
    haystack.includes('rpc error') ||
    haystack.includes('429')
  ) {
    return 'Network connection failed. Check your internet and try again.';
  }

  // ── Gas estimate failed (generic viem `EstimateGasExecutionError`) ───
  // Run this last because the other patterns above are also strictly
  // gas-estimate failures we want to surface specifically.
  if (
    haystack.includes('estimategas') ||
    haystack.includes('gas estimation failed')
  ) {
    return 'Could not estimate the gas fee. The recipient address or amount may be invalid.';
  }

  // ── Unknown — surface the raw short message so a bug report stays useful.
  return rawMessage(err) || fallback;
}
